import {
  ReactElement,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Alert,
  Skeleton,
  Container,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material'
import { FileDownload as ExportIcon } from '@mui/icons-material'
import { AddIcon, InfoIcon } from 'src/v3/core/icons'
import { useAppSelector } from 'src/store/hooks'
import { selectLinkedServices } from 'src/v3/settings/settings.slice'
import { useCertificatesRollup } from './hooks/use-certificates-rollup'
import { useCertificatesSummary } from './hooks/use-certificates-summary'
import { useCertificatesDateFilter } from './hooks/use-certificates-date-filter'
import {
  computeAccreditationBreakdown,
  computeCategoryBreakdownForType,
  computeSummaryFromRollup,
  filterRollupByDateRange,
} from './utils/compute-summary'
import { CertificateRollupItem } from './types'
import CertificatesDataGrid from './components/certificates-data-grid'
import CertificatesSummaryOverview from './components/certificates-summary-overview'
import ActivityBreakdown from './components/activity-breakdown'
import CertificatesDateFilter from './components/certificates-date-filter'
import ExternalCertificateUploadModal from './components/external-certificate-upload-modal'

let ACTIVITY_TYPE_LABELS: Record<CertificateRollupItem['type'], string> = {
  recorded: 'Recorded Courses',
  live: 'Live Classes',
  external: 'External Certificates',
  event: 'Events',
  attendance: 'Attendance Only',
}

export default function CertificatesPage(): ReactElement {
  let {
    data,
    isLoading,
    isError,
    refetch: refetchRollup,
  } = useCertificatesRollup()
  let {
    data: summaryData,
    isLoading: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useCertificatesSummary()

  let [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [selectedType, setSelectedType] = useState<
    CertificateRollupItem['type'] | null
  >(null)
  let [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  let dateFilter = useCertificatesDateFilter()
  let { queryParams } = dateFilter

  // Defer the range so recomputation runs off the critical render; while the
  // deferred value lags behind, sections show skeletons instead of janking.
  let deferredQueryParams = useDeferredValue(queryParams)
  let isRecomputing = queryParams !== deferredQueryParams

  let linkedServices = useAppSelector(selectLinkedServices)
  let hasLinkedService = linkedServices.length > 0

  let gridSectionRef = useRef<HTMLDivElement | null>(null)

  // Rollup items within the selected date range (all-time when no range set).
  let filteredRollup = useMemo(
    () =>
      filterRollupByDateRange(
        data || [],
        deferredQueryParams.startDate,
        deferredQueryParams.endDate,
      ),
    [data, deferredQueryParams],
  )

  let hasDateFilter = Boolean(
    deferredQueryParams.startDate || deferredQueryParams.endDate,
  )

  // With no active range, reuse the server summary so the all-time view is
  // identical to the unfiltered numbers; otherwise recompute client-side.
  // If the summary request fails, fall back to recomputing from the rollup
  // so the totals and activity breakdown still render.
  let useServerSummary = !hasDateFilter && !summaryError
  let computedSummary = useMemo(() => {
    if (useServerSummary) return summaryData
    if (!data) return undefined
    return computeSummaryFromRollup(filteredRollup)
  }, [useServerSummary, summaryData, data, filteredRollup])

  // Drop the drill-down selection if the type no longer appears in range.
  useEffect(() => {
    if (
      selectedType &&
      !filteredRollup.some((item) => item.type === selectedType)
    ) {
      setSelectedType(null)
    }
  }, [filteredRollup, selectedType])

  // Category breakdown is scoped to the selected type, so it has to clear
  // whenever the type selection changes or disappears.
  let categoryBreakdown = useMemo(
    () =>
      selectedType
        ? computeCategoryBreakdownForType(filteredRollup, selectedType)
        : [],
    [filteredRollup, selectedType],
  )

  useEffect(() => {
    setSelectedCategory(null)
  }, [selectedType])

  let accreditationBreakdown = useMemo(
    () => computeAccreditationBreakdown(filteredRollup),
    [filteredRollup],
  )

  let handleSelectType = (type: CertificateRollupItem['type']) => {
    setSelectedType((prev) => (prev === type ? null : type))
    gridSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  let handleSelectCategory = (categoryName: string) => {
    setSelectedCategory((prev) => (prev === categoryName ? null : categoryName))
  }

  // When the summary is recomputed client-side from the rollup (date filter
  // active, or the summary request failed) it no longer depends on the
  // summary endpoint — gating on `summaryLoading` there would needlessly hold
  // skeletons (and the filtered empty state) behind a slow summary request.
  let sectionsLoading =
    (useServerSummary ? summaryLoading : false) || isLoading || isRecomputing
  let showEmptyState =
    hasDateFilter && !sectionsLoading && filteredRollup.length === 0

  if (isError) {
    return (
      <Card sx={{ p: 3, backgroundColor: 'background.paper' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Certificate Data Error
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Unable to load your certificate data from the server.
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Try refreshing the page. If the problem persists, contact support.
          </Typography>
        </Alert>
      </Card>
    )
  }

  return (
    <Container maxWidth="xl">
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
            Certificates and Learning Summary
          </Typography>
          <Typography color="text.secondary" variant="body1">
            View and export all your learning achievements including
            certificates, CE hours, and completion records.
          </Typography>
          {computedSummary &&
            computedSummary.totalStats.totalActivities > 0 && (
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  mt: 1,
                }}
              >
                {computedSummary.totalStats.totalActivities} activities •{' '}
                {computedSummary.totalStats.totalCEHours} total CE hours
                {hasDateFilter ? ' (filtered)' : ''}
              </Typography>
            )}
        </Box>

        {/* Date Range Filter */}
        <CertificatesDateFilter
          startDate={dateFilter.startDate}
          endDate={dateFilter.endDate}
          today={dateFilter.today}
          hasActiveFilter={dateFilter.hasActiveFilter}
          isInvalidRange={dateFilter.isInvalidRange}
          onStartDateChange={dateFilter.updateStartDate}
          onEndDateChange={dateFilter.updateEndDate}
          onClear={dateFilter.clearFilter}
        />

        {showEmptyState ? (
          <Box sx={{ mb: 4, textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No activities found for the selected date range
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting or clearing the date range to see more activities.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Overview */}
            <CertificatesSummaryOverview
              data={computedSummary}
              isLoading={sectionsLoading}
            />

            {/* Activity Breakdown */}
            <ActivityBreakdown
              data={computedSummary}
              isLoading={sectionsLoading}
              hasLinkedService={hasLinkedService}
              selectedType={selectedType}
              onSelectType={handleSelectType}
              categoryBreakdown={categoryBreakdown}
              selectedCategory={selectedCategory}
              onSelectCategory={handleSelectCategory}
              accreditationBreakdown={accreditationBreakdown}
            />
          </>
        )}

        <Card
          ref={gridSectionRef}
          sx={{ p: 3, backgroundColor: 'background.paper' }}
        >
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <ExportIcon />
                Certificates & Learning Records
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setUploadModalOpen(true)}
                >
                  Upload External Certificate
                </Button>
                <Tooltip title="Upload certificates from other sources to centralize your educational journey in one place">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ maxWidth: 600 }}
            >
              All your continuing education data in one place. Use the toolbar
              to filter, search, and export to CSV format.
            </Typography>
            {selectedType && (
              <Box
                sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Filtered to:
                </Typography>
                <Chip
                  label={ACTIVITY_TYPE_LABELS[selectedType]}
                  variant="outlined"
                  color="primary"
                  onDelete={() => setSelectedType(null)}
                />
                {selectedCategory && (
                  <Chip
                    label={selectedCategory}
                    variant="outlined"
                    color="primary"
                    onDelete={() => setSelectedCategory(null)}
                  />
                )}
              </Box>
            )}
          </Box>

          {isLoading || isRecomputing ? (
            <Box role="status" aria-live="polite" aria-busy="true">
              <Skeleton variant="rectangular" sx={{ height: 600 }} />
              <span className="sr-only">Loading certificates...</span>
            </Box>
          ) : (
            <CertificatesDataGrid
              data={filteredRollup}
              isLoading={isLoading}
              hasLinkedService={hasLinkedService}
              typeFilter={selectedType}
              categoryFilter={selectedCategory}
              hasDateFilter={hasDateFilter}
            />
          )}
        </Card>
      </Box>
      <ExternalCertificateUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          refetchRollup()
          refetchSummary()
        }}
        onError={setErrorMessage}
      />
      <Snackbar
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        aria-live="polite"
        aria-atomic="true"
      >
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
          role="alert"
          aria-live="assertive"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}
